import React, { createContext, useState, useContext, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import { userService } from '../services/userService';
import api from '../services/api';
import i18n from '../i18n';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};

const applyUserLanguage = async () => {
  try {
    const response = await userService.getPreferences();
    const lang = response?.data?.language;
    if (lang) {
      i18n.changeLanguage(lang);
    }
  } catch {
    // silently fall back to localStorage language
  }
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Initialize authentication on mount
  useEffect(() => {
    const initializeAuth = async () => {
      const token = localStorage.getItem('accessToken');
      
      if (token) {
        try {
          
          // The authorization header is handled by the axios interceptor
          // api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          
          const response = await userService.getProfile();
          
          if (response.success) {
            setCurrentUser({
              ...response.data,
              accountId: response.data.accountId // Ensure accountId is present
            });
            setError('');
            applyUserLanguage();
          } else {
            throw new Error('Failed to get profile');
          }
        } catch (error) {
          const errorMessage = error.response?.data?.error || 'Session expired. Please login again.';
          // Clear invalid tokens
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          delete api.defaults.headers.common['Authorization'];
          setCurrentUser(null);
          setError(errorMessage);
        }
      }
      
      setLoading(false);
    };

    initializeAuth();
  }, []);

  // setAuthenticatedUser must be defined before handleTraditionalLogin, which
  // depends on it — useCallback's dependency array is evaluated at render
  // time, unlike a plain closure, so declaration order matters here.
  const setAuthenticatedUser = useCallback((user, accessToken, refreshToken) => {
    localStorage.setItem('accessToken', accessToken);
    if (refreshToken) {
      localStorage.setItem('refreshToken', refreshToken);
    }
    api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
    setCurrentUser({
      ...user,
      accountId: user.accountId
    });
    setError('');
    applyUserLanguage();
  }, []);

  const handleTraditionalLogin = useCallback(async (credentials) => {
    try {
      setError('');
      setLoading(true);

      const response = await userService.login(credentials);

      if (response.success) {
        const { user, accessToken, refreshToken } = response.data;

        setAuthenticatedUser(user, accessToken, refreshToken);

        return {
          success: true,
          user,
          accessToken,
          refreshToken
        };
      } else {
        const errorMsg = response.message || 'Login failed';
        setError(errorMsg);
        return { success: false, error: errorMsg };
      }
    } catch (error) {
      const errorMsg = error.response?.data?.message || error.message || 'Login failed';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  }, [setAuthenticatedUser]);

  // Logout user
  const logout = useCallback(async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (refreshToken) {
        await userService.logout(refreshToken);
      }
    } finally {
      setCurrentUser(null);
      setError('');
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      // The axios interceptor will handle the header
    }
  }, []);

  // Update current user
  const updateUser = useCallback((userData) => {
    setCurrentUser(prev => ({ ...prev, ...userData }));
  }, []);

  // Clear error
  const clearError = useCallback(() => {
    setError('');
  }, []);

  // ✅ Enhanced Permission check helper
  const hasPermission = useCallback((permission) => {
    // Master admin has all permissions
    if (currentUser?.isMasterAdmin) {
      return true;
    }

    if (permission === PERMISSIONS.MANAGE_ACCOUNT_USERS &&
        (currentUser?.role || '').toUpperCase() === 'ACCOUNT_ADMIN') {
      return true;
    }

    // Check if user has the specific permission
    if (currentUser?.Permissions) {
      return currentUser.Permissions.some(p => p.keyName === permission);
    }

    return false;
  }, [currentUser]);

  const requirePermission = useCallback((
    permission,
    message = "Vous n'avez pas l'autorisation pour cette action."
  ) => {
    if (hasPermission(permission)) {
      return true;
    }

    toast.error(message);
    return false;
  }, [hasPermission]);

  // ✅ Enhanced Role check helper
  const hasRole = useCallback((role) => {
    if (role === 'MASTER_ADMIN') {
      return currentUser?.isMasterAdmin === true;
    }
    return currentUser?.role === role;
  }, [currentUser]);

  // 🔐 BACKUP SPECIFIC AUTHORIZATION METHODS
  // Derived fresh each render from currentUser — not memoized themselves,
  // but every callback below lists currentUser as its dependency, which is
  // the only thing these are derived from, so that's sufficient.
  const roleLower = (currentUser?.role || '').toLowerCase();
  const isAccountAdmin = roleLower === 'account_admin';

  // Check if user can export data
  const canExport = useCallback((scope = 'account', targetAccountId = null) => {
    // Users cannot export at all
    if (roleLower === 'user') {
      return false;
    }

    // MASTER_ADMIN can export everything
    if (currentUser?.isMasterAdmin || roleLower === 'master_admin') {
      return true;
    }

    // ADMIN can only export account scope
    if (roleLower === 'admin' || isAccountAdmin) {
      if (scope === 'global') {
        return false;
      }

      // For account scope, check if targeting their own account
      if (targetAccountId) {
        return targetAccountId == currentUser?.accountId;
      }

      return true; // No specific account targeted
    }

    return false;
  }, [currentUser]);

  // Check if user can import data
  const canImport = useCallback((scope = 'account', targetAccountId = null) => {
    // Master admin can always import
    if (currentUser?.isMasterAdmin || roleLower === 'master_admin') {
      return true;
    }

    // Check scope permissions
    if (scope === 'global') {
      return hasPermission(PERMISSIONS.RESTORE_GLOBAL);
    }

    // For account scope, check if user has account restore permission
    // AND if they're trying to import to their own account
    if (scope === 'account') {
      const hasAccountPermission = hasPermission(PERMISSIONS.RESTORE);

      // If no specific account is targeted, allow if they have account permission
      if (!targetAccountId) {
        return hasAccountPermission;
      }

      // If specific account is targeted, check if it's their own account
      return hasAccountPermission && targetAccountId === currentUser?.accountId;
    }

    return canExport(scope, targetAccountId);
  }, [currentUser, hasPermission, canExport]);

  // Get user's allowed account IDs for backup operations
  const getAllowedAccountIds = useCallback(() => {
    if (currentUser?.isMasterAdmin || roleLower === 'master_admin') {
      return null; // null means all accounts are allowed
    }
    return [currentUser?.accountId]; // Only their own account
  }, [currentUser]);

  // Check if user can manage backups (view backup logs, etc.)
  const canManageBackups = useCallback(() => {
    return roleLower === 'master_admin' || roleLower === 'admin' || isAccountAdmin;
  }, [currentUser]);

  // Get user's backup scope capabilities
  const getBackupCapabilities = useCallback(() => {
    const isMasterAdmin = currentUser?.isMasterAdmin || roleLower === 'master_admin';
    const isAdmin = roleLower === 'admin' || isAccountAdmin;
    const isUser = roleLower === 'user';

    return {
      canExportAccount: isMasterAdmin || isAdmin,
      canExportGlobal: isMasterAdmin,
      canImportAccount: isMasterAdmin || isAdmin,
      canImportGlobal: isMasterAdmin,
      canManageBackups: isMasterAdmin || isAdmin,
      allowedAccountIds: isMasterAdmin ? null : [currentUser?.accountId],
      isMasterAdmin: isMasterAdmin,
      isAdmin: isAdmin,
      isUser: isUser
    };
  }, [currentUser]);

  const value = useMemo(() => ({
    currentUser,
    handleTraditionalLogin, // Expose the new traditional login function
    setAuthenticatedUser,   // Expose the new function to set user from external auth
    logout,
    updateUser,
    hasPermission,
    requirePermission,
    hasRole,

    // Backup-specific authorization methods
    canExport,
    canImport,
    canManageBackups,
    getAllowedAccountIds,
    getBackupCapabilities,

    loading,
    error,
    clearError
  }), [
    currentUser,
    handleTraditionalLogin,
    setAuthenticatedUser,
    logout,
    updateUser,
    hasPermission,
    requirePermission,
    hasRole,
    canExport,
    canImport,
    canManageBackups,
    getAllowedAccountIds,
    getBackupCapabilities,
    loading,
    error,
    clearError
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const PERMISSIONS = {
  // Dashboard & Overview
  VIEW_DASHBOARD: 'view_dashboard',
  VIEW_OVERVIEW: 'view_overview',
  
  // Account Management
  MANAGE_ACCOUNTS: 'manage_accounts',
  VIEW_ACCOUNTS: 'view_accounts',
  
  // User Management
  MANAGE_ACCOUNT_USERS: 'manage_account_users',
  MANAGE_USERS: 'manage_users',
  VIEW_USERS: 'view_users',
  CREATE_USERS: 'create_users',
  EDIT_USERS: 'edit_users',
  DELETE_USERS: 'delete_users',
  ASSIGN_USERS_TO_ACCOUNTS: 'assign_users_to_accounts',
  
  // Car Management (your existing ones are perfect)
  VIEW_CARS: 'view_cars',
  CREATE_CARS: 'create_cars',
  EDIT_CARS: 'edit_cars',
  DELETE_CARS: 'delete_cars',
  
  // Reservation Management (perfect)
  VIEW_RESERVATIONS: 'view_reservations',
  CREATE_RESERVATIONS: 'create_reservations',
  EDIT_RESERVATIONS: 'edit_reservations',
  DELETE_RESERVATIONS: 'delete_reservations',
  
  // Client Management (perfect)
  VIEW_CLIENTS: 'view_clients',
  CREATE_CLIENTS: 'create_clients',
  EDIT_CLIENTS: 'edit_clients',
  DELETE_CLIENTS: 'delete_clients',
  
  // Maintenance Management (perfect)
  VIEW_MAINTENANCE: 'view_maintenance',
  CREATE_MAINTENANCE: 'create_maintenance',
  EDIT_MAINTENANCE: 'edit_maintenance',
  DELETE_MAINTENANCE: 'delete_maintenance',
  
  // Reports
  VIEW_REPORTS: 'view_reports',
  EXPORT_REPORTS: 'export_reports',
  
  // Settings
  VIEW_SETTINGS: 'view_settings',
  EDIT_SETTINGS: 'edit_settings',
  
  // Role Management
  MANAGE_ROLES: 'manage_roles',
  ASSIGN_ROLES: 'assign_roles',
  VIEW_ROLES: 'view_roles',
  
  // Audit Logs
  VIEW_AUDIT_LOGS: 'view_audit_logs',
  VIEW_ACCOUNT_AUDIT_LOGS: 'view_account_audit_logs',
  
  // Financials
  VIEW_FINANCIALS: 'view_financials',
  MANAGE_FINANCIALS: 'manage_financials',
  VIEW_REVENUE: 'view_revenue',
  VIEW_EXPENSES: 'view_expenses',
  MANAGE_PRICING: 'manage_pricing',
  
  // System
  MANAGE_SETTINGS: 'manage_settings',
  EXPORT_DATA: 'export_data',
  VIEW_SYSTEM_LOGS: 'view_system_logs',
  
  // Notifications
  MANAGE_NOTIFICATIONS: 'manage_notifications',
  SEND_NOTIFICATIONS: 'send_notifications',
  
  // Data Export (more granular)
  EXPORT_CARS: 'export_cars',
  EXPORT_CLIENTS: 'export_clients',
  EXPORT_RESERVATIONS: 'export_reservations',
  EXPORT_MAINTENANCE: 'export_maintenance',

  // Backup & Restore Permissions
  BACKUP: 'backup',
  RESTORE: 'restore',
  BACKUP_GLOBAL: 'backup_global',
  RESTORE_GLOBAL: 'restore_global',
  MANAGE_BACKUPS: 'manage_backups'
};
